import { FormProvider } from "react-hook-form";
import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
} from "react";
import { Company, DeliveryType } from "$types";
import { useAssignCarrierLogic } from "../hooks/useAssignCarrierLogic";
import useCompanyForm from "$hooks/useCompanyForm";

type CompanyTypeField = keyof Pick<
  AssignCarrierFormValues,
  "agencyCompanyId" | "commissionCompanyId" | "carrierCompanyId"
>;

export type AssignCarrierFormValues = {
  carrierCompanyId: string;
  commissionCompanyId: string;
  agencyCompanyId: string;
  deliveryType: DeliveryType;
};

type AssignCarrierContextType = {
  isOpen: boolean;
  error: string;
  setError: (error: string) => void;
  defaultValues: AssignCarrierFormValues;
  selectedCompanyTypeToCreate: CompanyTypeField;
  setSelectedCompanyTypeToCreate: (type: CompanyTypeField) => void;
  handleAddCompanyClick: (companyTypeField: CompanyTypeField) => void;
  handleOnCompanyCreated: (
    companyId: string,
    companyForm: Company
  ) => Promise<void>;
  isCommissionCompanySelectDisplayed: boolean;
  isCarrierCompanySelectDisplayed: boolean;
  isAgencyCompanySelectDisplayed: boolean;
  isCommissionCompanySelectRequired: boolean;
  isCarrierCompanySelectRequired: boolean;
  isAgencyCompanySelectRequired: boolean;
  selectedCarrierCompany: Company | undefined;
  agencyOptions: any[];
  carrierOptions: any[];
  refetchCompanies: () => Promise<void>;
  resetCarrier: () => void;
  // Add company form related props
  isCreatingCompany: boolean;
  handleCompanySubmit: (data: any) => Promise<void>;
  toggleCompanyDrawer: (isOpen: boolean) => void;
  isCompanyDrawerOpen: boolean;
  companyFormError: any;
};

export const AssignCarrierContext = createContext<
  AssignCarrierContextType | undefined
>(undefined);

export const useAssignCarrier = () => {
  const context = useContext(AssignCarrierContext);
  if (context === undefined) {
    throw new Error(
      "useAssignCarrier must be used within AssignCarrierProvider"
    );
  }
  return context;
};

type AssignCarrierProviderProps = {
  children: React.ReactNode;
  isOpen: boolean;
  defaultValues: AssignCarrierFormValues;
  resetCarrier: () => void;
};

export const AssignCarrierProvider: React.FC<AssignCarrierProviderProps> = ({
  children,
  isOpen,
  defaultValues,
  resetCarrier,
}) => {
  const [error, setError] = useState<string>(undefined);
  const {
    form,
    isCommissionCompanySelectDisplayed,
    isCarrierCompanySelectDisplayed,
    isAgencyCompanySelectDisplayed,
    isCommissionCompanySelectRequired,
    isCarrierCompanySelectRequired,
    isAgencyCompanySelectRequired,
    selectedCarrierCompany,
    agencyOptions,
    carrierOptions,
    refetchCompanies,
    handleOnCompanyCreated,
    selectedCompanyTypeToCreate,
    setSelectedCompanyTypeToCreate,
  } = useAssignCarrierLogic(defaultValues);

  const {
    submitting: isCreatingCompany,
    handleSubmit: handleCompanySubmit,
    togglePopin: toggleCompanyDrawer,
    isOpened: isCompanyDrawerOpen,
    error: companyFormError,
  } = useCompanyForm(handleOnCompanyCreated);

  const handleAddCompanyClick = useCallback(
    (companyTypeField: CompanyTypeField) => {
      setSelectedCompanyTypeToCreate(companyTypeField);
      toggleCompanyDrawer(true);
    },
    [setSelectedCompanyTypeToCreate, toggleCompanyDrawer]
  );

  const value = useMemo(
    () => ({
      isOpen,
      error,
      setError,
      defaultValues,
      selectedCompanyTypeToCreate,
      setSelectedCompanyTypeToCreate,
      handleAddCompanyClick,
      handleOnCompanyCreated,
      isCommissionCompanySelectDisplayed,
      isCarrierCompanySelectDisplayed,
      isAgencyCompanySelectDisplayed,
      isCommissionCompanySelectRequired,
      isCarrierCompanySelectRequired,
      isAgencyCompanySelectRequired,
      selectedCarrierCompany,
      agencyOptions,
      carrierOptions,
      refetchCompanies,
      resetCarrier,
      // Add company form values
      isCreatingCompany,
      handleCompanySubmit,
      toggleCompanyDrawer,
      isCompanyDrawerOpen,
      companyFormError,
    }),
    [
      isOpen,
      error,
      defaultValues,
      selectedCompanyTypeToCreate,
      setSelectedCompanyTypeToCreate,
      handleAddCompanyClick,
      handleOnCompanyCreated,
      isCommissionCompanySelectDisplayed,
      isCarrierCompanySelectDisplayed,
      isAgencyCompanySelectDisplayed,
      isCommissionCompanySelectRequired,
      isCarrierCompanySelectRequired,
      isAgencyCompanySelectRequired,
      selectedCarrierCompany,
      agencyOptions,
      carrierOptions,
      refetchCompanies,
      resetCarrier,
      isCreatingCompany,
      handleCompanySubmit,
      toggleCompanyDrawer,
      isCompanyDrawerOpen,
      companyFormError,
    ]
  );

  return (
    <AssignCarrierContext.Provider value={value}>
      <FormProvider {...form}>{children}</FormProvider>
    </AssignCarrierContext.Provider>
  );
};
